import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, type Product } from '@/services/api';

// 💡 新增：處理時區偏移的日期格式化函式
const formatLocalYYYYMMDD = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  // 如果遇到無法解析的日期，退回原本的字串切割法
  if (isNaN(d.getTime())) return String(dateString).split('T')[0];
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Product() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        const rawData: any = await api.getProductById(Number(id));
        
        // 💡 變更：使用新的格式化函式處理發行日期
        const rawDate = rawData.release_date || rawData.releaseDate;
        const displayDate = formatLocalYYYYMMDD(rawDate);
        
        const formattedProduct: Product = {
          ...rawData,
          isPreorder: rawData.is_preorder,
          keyDeliveryDate: rawData.key_delivery_date,
          nameEn: rawData.name_en || rawData.nameEn,
          originalPrice: rawData.original_price || rawData.originalPrice,
          salePrice: rawData.sale_price || rawData.salePrice,
          image: rawData.image_url || rawData.image,
          releaseDate: displayDate,
          steamDeck: rawData.steam_deck || rawData.steamDeck,
          steamController: rawData.steam_controller || rawData.steamController,
        };

        setProduct(formattedProduct);
      } catch (err: any) {
        setError(err.message || '找不到遊戲');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (id) fetchProduct();
  }, [id]);

  if (isLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-text-muted">載入遊戲資訊中...</div>;
  }

  if (error || !product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <i className="ri-error-warning-line text-6xl text-text-gray mb-4"></i>
        <h2 className="text-text-dark text-xl font-heading font-bold mb-2">找不到遊戲</h2>
        <p className="text-text-muted mb-6">這個遊戲可能已經下架或不存在了喔～</p>
        <Link to="/" className="text-accent-orange hover:underline text-sm cursor-pointer">回到首頁</Link>
      </div>
    );
  }

  const handleBuy = () => {
    navigate('/checkout', { state: { product } });
  };

  const isPreorder = product.isPreorder ?? (product as any).is_preorder;
  const isPreorderBool = Number(isPreorder) === 1;
  
  // 💡 變更：使用新的格式化函式處理預售發送日期
  const deliveryDate = (product as any).keyDeliveryDate || (product as any).key_delivery_date;
  const displayDeliveryDate = formatLocalYYYYMMDD(deliveryDate);

  return (
    <div className="w-full">
      {/* Breadcrumb */}
      <div className="w-full px-6 md:px-10 py-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-text-muted">
          <Link to="/" className="hover:text-text-dark transition-colors cursor-pointer">首頁</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-text-dark">{product.name}</span>
        </div>
      </div>

      {/* Product Main */}
      <div className="w-full px-6 md:px-10 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Left: Image */}
            <div className="lg:w-5/12">
              <div className="relative aspect-square rounded-xl overflow-hidden bg-fog-mid">
                {isPreorderBool && (
                  <div className="absolute top-0 left-0 z-10 w-32 h-32 overflow-hidden pointer-events-none">
                    <span className="absolute top-7 -left-10 w-40 -rotate-45 bg-accent-orange text-white text-sm font-bold text-center py-1.5 shadow-md whitespace-nowrap">
                      預售
                    </span>
                  </div>
                )}
                <img
                  src={product.image}
                  alt={product.name}
                  title={`${product.name} - Steam Key`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Right: Info */}
            <div className="lg:w-7/12">
              <span className="inline-block text-xs text-accent-orange/80 font-medium tracking-widest uppercase mb-3 bg-accent-orange/10 px-3 py-1 rounded-full">
                STEAM KEY
              </span>
              <h1 className="text-text-dark text-2xl md:text-4xl font-heading font-bold mb-2 tracking-wide">
                {product.name}
              </h1>
              <p className="text-text-muted text-sm mb-6">{product.nameEn}</p>

              {/* Price */}
              <div className="flex items-center gap-4 mb-8 p-5 rounded-xl bg-fog border border-fog-mid">
                <div className="flex flex-col">
                  <span className="text-text-gray text-sm line-through">NT${product.originalPrice}</span>
                  <span className="text-accent-orange text-2xl md:text-3xl font-bold">NT${product.salePrice}</span>
                </div>
                <div className="bg-discount-yellow text-white text-sm font-bold px-3 py-2 rounded-lg whitespace-nowrap">
                  -{product.discount}%
                </div>
              </div>

              {/* Buy Area (加入預售文字提示) */}
              <div className="mb-10">
                <button
                  onClick={handleBuy}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-accent-orange hover:bg-accent-orange/90 text-white text-sm font-semibold px-10 py-3.5 rounded-full transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-cart-2-line text-lg"></i>
                  {isPreorderBool ? '立即預購' : '立即購買'}
                </button>
                
                {/* 💡 Highlight 1：按鈕下方的溫馨提示 */}
                {isPreorderBool && displayDeliveryDate && (
                  <p className="mt-3 text-sm text-accent-orange font-medium flex items-center gap-1.5">
                    <i className="ri-information-fill text-base"></i>
                    此為預售商品，遊戲序號預計將於 {displayDeliveryDate} 統一發送
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="mb-10">
                <h3 className="text-text-dark text-lg font-heading font-semibold mb-3">商品介紹</h3>
                <p className="text-text-muted text-sm leading-relaxed">{product.description}</p>
              </div>

              {/* Game Details */}
              <div>
                <h3 className="text-text-dark text-lg font-heading font-semibold mb-4">遊戲詳情</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  
                  {/* 💡 Highlight 2：在詳情列表中放大並改變顏色 */}
                  {isPreorderBool && displayDeliveryDate && (
                    <div className="sm:col-span-2">
                      <DetailItem 
                        label="序號發送日期" 
                        value={displayDeliveryDate} 
                        highlight={true} 
                      />
                    </div>
                  )}

                  <DetailItem label="平台" value={product.platform || ''} />
                  <DetailItem label="發行日期" value={product.releaseDate || ''} />
                  <DetailItem label="開發者" value={product.developer || ''} />
                  <DetailItem label="發行商" value={product.publisher || ''} />
                  <DetailItem label="Steam Deck 支援" value={product.steamDeck || ''} />
                  <DetailItem label="Steam 控制器支援" value={product.steamController || ''} />
                  <div className="sm:col-span-2">
                    <DetailItem
                      label="支援語言"
                      value={product.languages?.join('、') || ''}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 💡 變更：新增 highlight 屬性，用來切換特別顯眼的樣式
function DetailItem({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${
      highlight 
        ? 'bg-accent-orange/10 border-accent-orange/30' 
        : 'bg-fog border-transparent'
    }`}>
      <span className={`text-sm whitespace-nowrap min-w-[100px] ${
        highlight ? 'text-accent-orange font-bold' : 'text-text-muted'
      }`}>
        {highlight && <i className="ri-calendar-check-line mr-1"></i>}
        {label}
      </span>
      <span className={`text-sm ${
        highlight ? 'text-accent-orange font-bold' : 'text-text-dark'
      }`}>
        {value}
      </span>
    </div>
  );
}